import {
  CuratedProductListResponseType,
  DiscoveryProductsRecentlyAddedResponseType,
} from "@sellar-io/api-v2/types";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { SpinnerLegacy } from "../../../components";
import { BestPricePromiseDialog } from "../../../components/bestPricePromise/BestPricePromiseDialog";
import { useProductCuratedList } from "../../../hooks/api/v2/product/useProductCuratedList";
import { useEvents } from "../../../hooks/events/useEvents";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { getSellarWebsiteUtms } from "../../../utils/utmHelper";
import { DiscoveryProductDialog } from "../../discovery/v2/components/DiscoveryProductDialog";
import {
  useDiscoveryV2RecentlyViewedStore,
  setRecentlyViewedExpiry,
} from "../../discovery/v2/DiscoveryV2RecentlyViewedStore";
import { SpotlightProductCard } from "./SpotlightProductCard";
import { DiscoveryHorizontalList } from "../../discovery/v2/components/DiscoveryHorizontalList";

type SpotlightProduct = CuratedProductListResponseType[number];

type SpotlightSupplierWithProducts = {
  id: number;
  imageUrl: string | null;
  urlId: string;
  Business: { name: string; logoUrl: string | null };
  products: SpotlightProduct[];
};

export const SpotlightProductBySupplierList = ({ ids }: { ids: number[] }) => {
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const [selectedProduct, setSelectedProduct] = useState<
    DiscoveryProductsRecentlyAddedResponseType[number] | undefined
  >();
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState(false);

  const productListQuery = useProductCuratedList(
    {
      query: [
        {
          type: "id",
          modifier: "IN",
          value: ids,
        },
      ],
      orderBy: "random",
    },
    {
      staleTime: 1000 * 60 * 10,
    },
  );

  const productList =
    productListQuery.data?.pages.flatMap((page) => page) ?? [];

  const groupProductsBySupplier = (products: SpotlightProduct[]) => {
    const result: SpotlightSupplierWithProducts[] = [];

    products.forEach((product) => {
      let supplierGroup = result.find(
        (group) => group.id === product.Supplier.id,
      );

      if (!supplierGroup) {
        // If the supplier doesn't exist, create a new group
        supplierGroup = {
          ...product.Supplier,
          products: [],
        };
        result.push(supplierGroup);
      }

      // Add the product to the supplier's products list
      supplierGroup.products.push(product);
    });

    return result;
  };

  const isLoading = productListQuery.isLoading;

  const supplierList = groupProductsBySupplier(productList);

  const onProductClicked = (
    product: DiscoveryProductsRecentlyAddedResponseType[number],
  ) => {
    if (product.Supplier) {
      setSelectedProduct(product);
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: product.id,
        supplierId: product.Supplier.id,
        name: product.name ?? "",
        imageUrl: product.imageUrl,
        logoUrl: product.Supplier.Business?.logoUrl,
        urlId: product.Supplier.urlId,
        details: `${product.kind} // ${product.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: product.containsPreOrderProductItem,
      });

      track("Discover product clicked", {
        supplier: product.Supplier.urlId,
        product: product.name,
        source: "Recently added",
        containsPreOrderProductItem: product.containsPreOrderProductItem,
        sessionId,
        ...getSellarWebsiteUtms(),
      });
    }
  };

  return (
    <>
      <div className={"px-4 pb-4"}>
        {isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            <div className={twMerge("grid grid-cols-1 gap-4 sm:grid-cols-2")}>
              {supplierList.map((supplier, idx) => (
                <div
                  className="flex flex-col gap-2 rounded-lg bg-white p-4"
                  key={idx}
                >
                  <div>
                    <span>{supplier.Business.name}</span>
                  </div>
                  <DiscoveryHorizontalList>
                    {supplier.products.map((product) => (
                      <SpotlightProductCard
                        key={product.id}
                        product={product}
                        onClick={() => onProductClicked(product)}
                      />
                    ))}
                  </DiscoveryHorizontalList>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {selectedProduct && (
        <>
          <DiscoveryProductDialog
            productId={selectedProduct.id}
            supplierId={selectedProduct.Supplier?.id}
            active={!!selectedProduct}
            onDismiss={() => {
              setSelectedProduct(undefined);
            }}
            setBestPricePromiseDialogActive={() => {
              setBestPricePromiseDialogActive(true);
            }}
          />

          <BestPricePromiseDialog
            productName={selectedProduct.name}
            buyerName={selectedProduct.name}
            supplierName={selectedProduct.Supplier?.Business?.name}
            active={bestPricePromiseDialogActive}
            onDismiss={() => setBestPricePromiseDialogActive(false)}
          />
        </>
      )}
    </>
  );
};
